import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { dependentsAPI, storesAPI } from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Users, Plus, Trash2, Edit2, Store, Mail, User, Search, AlertCircle, CheckCircle2, Send, CalendarDays } from 'lucide-react';

export default function StoreDependentsPage() {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [dependents, setDependents] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sendingStoreSummary, setSendingStoreSummary] = useState(false);
  const [sendingAllSummaries, setSendingAllSummaries] = useState(false);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDependent, setEditingDependent] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadStoresWithCount();
  }, []);

  useEffect(() => {
    if (selectedStore) {
      loadDependents(selectedStore.id);
    } else {
      setDependents([]);
    }
  }, [selectedStore]);

  const loadStoresWithCount = async () => {
    try {
      setLoading(true);
      const response = await dependentsAPI.getStoresWithCount();
      setStores(response.data);
    } catch (error) {
      toast.error('Error al cargar tiendas');
    } finally {
      setLoading(false);
    }
  };

  const loadDependents = async (storeId) => {
    try {
      const response = await dependentsAPI.getByStore(storeId);
      setDependents(response.data);
    } catch (error) {
      toast.error('Error al cargar dependientes');
    }
  };

  const handleAddDependent = () => {
    setFormData({ name: '', email: '' });
    setEditingDependent(null);
    setShowAddModal(true);
  };

  const handleEditDependent = (dependent) => {
    setFormData({ name: dependent.name, email: dependent.email });
    setEditingDependent(dependent);
    setShowAddModal(true);
  };

  const handleDeleteDependent = async (dependent) => {
    if (!window.confirm(`¿Eliminar a ${dependent.name}?`)) return;
    
    try {
      await dependentsAPI.delete(selectedStore.id, dependent.id);
      toast.success('Dependiente eliminado');
      loadDependents(selectedStore.id);
      loadStoresWithCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Todos los campos son requeridos');
      return;
    }

    setSubmitting(true);
    try {
      if (editingDependent) {
        await dependentsAPI.update(selectedStore.id, editingDependent.id, formData);
        toast.success('Dependiente actualizado');
      } else {
        await dependentsAPI.create(selectedStore.id, formData);
        toast.success('Dependiente agregado');
      }
      setShowAddModal(false);
      loadDependents(selectedStore.id);
      loadStoresWithCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendStoreSummary = async () => {
    if (!selectedStore || !dependents.length) return;
    
    setSendingStoreSummary(true);
    try {
      const response = await dependentsAPI.sendStoreSummary(selectedStore.id);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar resumen');
    } finally {
      setSendingStoreSummary(false);
    }
  };

  const handleSendAllSummaries = async () => {
    if (!window.confirm('¿Enviar resumen mensual a TODOS los dependientes de todas las tiendas?')) return;
    
    setSendingAllSummaries(true);
    try {
      const response = await dependentsAPI.sendAllSummaries();
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar resúmenes');
    } finally {
      setSendingAllSummaries(false);
    }
  };

  const filteredStores = stores.filter(store =>
    store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    store.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Dependientes</h1>
          <p className="text-gray-600 mt-1">
            Administra los dependientes de cada tienda para recibir notificaciones automáticas
          </p>
        </div>
        <Button
          onClick={handleSendAllSummaries}
          disabled={sendingAllSummaries}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="send-all-summaries-button"
        >
          {sendingAllSummaries ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Enviando...
            </>
          ) : (
            <>
              <CalendarDays className="w-4 h-4 mr-2" />
              Enviar Resumen a Todos
            </>
          )}
        </Button>
      </div>

      {/* Info Alert */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">Notificaciones automáticas</p>
          <p className="mt-1">
            Cuando una solicitud sea aprobada o modificada, los dependientes de las tiendas incluidas 
            recibirán un email automático con todos los detalles. También puede enviar un resumen mensual manualmente.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Store List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="w-5 h-5" />
                Tiendas
              </CardTitle>
              <CardDescription>Selecciona una tienda para ver sus dependientes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar tienda..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-stores-input"
                />
              </div>
              
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {filteredStores.map((store) => (
                  <button
                    key={store.id}
                    onClick={() => setSelectedStore(store)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedStore?.id === store.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    data-testid={`store-item-${store.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{store.name}</p>
                        <p className="text-sm text-gray-500">{store.city}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {store.has_dependents ? (
                          <span className="flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                            <CheckCircle2 className="w-3 h-3" />
                            {store.dependents_count}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                            <AlertCircle className="w-3 h-3" />
                            Sin dep.
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
                
                {filteredStores.length === 0 && (
                  <p className="text-center text-gray-500 py-4">No se encontraron tiendas</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Dependents List */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    {selectedStore ? `Dependientes de ${selectedStore.name}` : 'Dependientes'}
                  </CardTitle>
                  <CardDescription>
                    {selectedStore 
                      ? `${dependents.length} dependiente(s) registrado(s)`
                      : 'Selecciona una tienda para ver y gestionar sus dependientes'
                    }
                  </CardDescription>
                </div>
                {selectedStore && (
                  <div className="flex gap-2">
                    {dependents.length > 0 && (
                      <Button 
                        variant="outline"
                        onClick={handleSendStoreSummary}
                        disabled={sendingStoreSummary}
                        data-testid="send-store-summary-button"
                      >
                        {sendingStoreSummary ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Enviar Resumen
                          </>
                        )}
                      </Button>
                    )}
                    <Button onClick={handleAddDependent} data-testid="add-dependent-button">
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!selectedStore ? (
                <div className="text-center py-12 text-gray-500">
                  <Store className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Selecciona una tienda de la lista para ver sus dependientes</p>
                </div>
              ) : dependents.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 mx-auto mb-3 text-amber-500 opacity-50" />
                  <p className="text-gray-600 font-medium">No hay dependientes registrados</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Agrega al menos un dependiente para habilitar las notificaciones
                  </p>
                  <Button onClick={handleAddDependent} className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Dependiente
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {dependents.map((dependent) => (
                    <div
                      key={dependent.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                      data-testid={`dependent-item-${dependent.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{dependent.name}</p>
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {dependent.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditDependent(dependent)}
                          data-testid={`edit-dependent-${dependent.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDependent(dependent)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-dependent-${dependent.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {editingDependent ? 'Editar Dependiente' : 'Agregar Dependiente'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nombre completo *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Juan Pérez"
                  required
                  data-testid="dependent-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email">Correo electrónico *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Ej: juan.perez@ejemplo.com"
                  required
                  data-testid="dependent-email-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={submitting} data-testid="save-dependent-button">
                  {submitting ? 'Guardando...' : editingDependent ? 'Actualizar' : 'Agregar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
